import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

interface PaginationProps {
    /** Current page, 1-indexed */
    page: number;
    pageSize: number;
    totalRecords: number;
    onPageChange: (page: number) => void;
    /** Domain word for the info label — defaults to "records". e.g. "assets", "users". */
    recordLabel?: string;
    /**
     * When provided, renders a "Show N" page-size dropdown at the bottom-left of the
     * footer and calls this on change. Pass alongside `pageSize` to make the page size
     * controllable. Omit to hide the dropdown (legacy behaviour).
     */
    onPageSizeChange?: (size: number) => void;
    /** Options for the page-size dropdown. Defaults to [10, 20, 50, 100]. */
    pageSizeOptions?: number[];
    /** Optional extra content rendered on the far left of the footer, before the page-size dropdown. */
    leftSlot?: React.ReactNode;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * Shared pagination footer used at the bottom of any table or list.
 * Layout: [Show N dropdown] "Showing 1–10 of 235 records" on the left,
 * page-nav buttons on the right.
 *
 * Pass `onPageSizeChange` to render the built-in "Show N" page-size dropdown at
 * the bottom-left (and remove any page-size dropdown from the page header). Omit
 * it for the legacy footer without a dropdown.
 */
const Pagination: React.FC<PaginationProps> = ({
    page,
    pageSize,
    totalRecords,
    onPageChange,
    recordLabel = 'records',
    onPageSizeChange,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    leftSlot,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const firstRecord = totalRecords === 0 ? 0 : (safePage - 1) * pageSize + 1;
    const lastRecord = Math.min(safePage * pageSize, totalRecords);

    const goto = (p: number) => {
        if (p < 1 || p > totalPages || p === safePage) return;
        onPageChange(p);
    };

    // Compact page list: 1 … 4 5 6 … 12
    const pageNumbers: (number | 'ellipsis')[] = (() => {
        const out: (number | 'ellipsis')[] = [];
        const range = (a: number, b: number) => {
            for (let i = a; i <= b; i++) out.push(i);
        };
        if (totalPages <= 7) {
            range(1, totalPages);
        } else {
            out.push(1);
            if (safePage > 4) out.push('ellipsis');
            const start = Math.max(2, safePage - 1);
            const end = Math.min(totalPages - 1, safePage + 1);
            range(start, end);
            if (safePage < totalPages - 3) out.push('ellipsis');
            out.push(totalPages);
        }
        return out;
    })();

    // Render nothing only when there's nothing to anchor (no size dropdown, no left
    // slot) and nothing to page through.
    if (!leftSlot && !onPageSizeChange && (totalRecords === 0 || totalPages <= 1)) return null;

    const showNav = totalRecords > 0;

    return (
        <div className="app-pagination">
            <div className="app-pagination-left">
                {leftSlot}
                {onPageSizeChange && (
                    <label className="filter-page-size-group">
                        <span className="filter-page-size-label">Show</span>
                        <select
                            value={String(pageSize)}
                            onChange={e => onPageSizeChange(parseInt(e.target.value, 10))}
                            className="fi fi--select filter-page-size"
                            data-testid="SEL_pagination_page_size"
                            aria-label="Rows per page"
                        >
                            {pageSizeOptions.map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    </label>
                )}
                <div className="app-pagination-info">
                    {totalRecords === 0
                        ? `No ${recordLabel}`
                        : `Showing ${firstRecord}–${lastRecord} of ${totalRecords} ${recordLabel}`}
                </div>
            </div>
            {showNav && (
                <div className="app-pagination-nav">
                    <button
                        type="button"
                        data-testid="BTN_pagination_first"
                        className="app-pagination-btn"
                        onClick={() => goto(1)}
                        disabled={safePage === 1}
                        aria-label="First page"
                    >
                        <ChevronsLeft size={14} />
                    </button>
                    <button
                        type="button"
                        data-testid="BTN_pagination_prev"
                        className="app-pagination-btn"
                        onClick={() => goto(safePage - 1)}
                        disabled={safePage === 1}
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    {pageNumbers.map((p, i) =>
                        p === 'ellipsis' ? (
                            <span key={`e${i}`} className="app-pagination-ellipsis">…</span>
                        ) : (
                            <button
                                key={p}
                                type="button"
                                data-testid={`BTN_pagination_page_${p}`}
                                className={`app-pagination-btn app-pagination-btn--page${p === safePage ? ' app-pagination-btn--active' : ''}`}
                                onClick={() => goto(p)}
                                aria-current={p === safePage ? 'page' : undefined}
                            >
                                {p}
                            </button>
                        ),
                    )}
                    <button
                        type="button"
                        data-testid="BTN_pagination_next"
                        className="app-pagination-btn"
                        onClick={() => goto(safePage + 1)}
                        disabled={safePage === totalPages}
                        aria-label="Next page"
                    >
                        <ChevronRight size={14} />
                    </button>
                    <button
                        type="button"
                        data-testid="BTN_pagination_last"
                        className="app-pagination-btn"
                        onClick={() => goto(totalPages)}
                        disabled={safePage === totalPages}
                        aria-label="Last page"
                    >
                        <ChevronsRight size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Pagination;
